import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Row, Spin, Typography, message } from 'antd';
import { RollbackOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import { useCompany } from '../../../contexts/CompanyContext';
import { useConfig } from '../../../contexts/ConfigContext';
import { useRegister } from '../../../contexts/RegisterContext';
import { useSecureRequest } from '../../../hooks/useSecureRequest';
import { IMovementRS, TReceiptData } from '../../../models/salesModel';
import { formatDateTime, getDocumentLabels, isFullReturnOrAnnulment, roundValue } from '../../../utils';
import RegisterBillPaymentModal from '../../RegisterBillPaymentModal';
import { useValidateResourceCompany } from '../../../hooks/useValidateResourceCompany';
import { getSaleGeneralStatus } from './ViewBillList';
import {
  getElectronicDocumentTitle,
  BillDetailHeaderBar,
  BillDetailItemsCard,
  BillDetailNotesCard,
  BillDetailPaymentsCard,
  BillDetailGeneralStatusCard,
  BillDetailElectronicInvoiceCard,
  BillDetailGeneralInfoCard,
  BillDetailEmailModal
} from './bill-detail';

const { Title } = Typography;

function ViewBillDetail() {
  const { uid } = useParams<{ uid: string }>();
  const { config } = useConfig();
  const { company } = useCompany();
  const { register } = useRegister();
  const { securePost } = useSecureRequest();
  const history = useHistory();
  const { validateResourceCompany, isSwitchingCompany } = useValidateResourceCompany();
  const [bill, setBill] = useState<IMovementRS | null>(null);
  const [notes, setNotes] = useState<IMovementRS[]>([]);
  const [loading, setLoading] = useState(true);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [emailTarget, setEmailTarget] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const notesReasons = [...register.creditNoteOptions, ...register.debitNoteOptions];

  const isFetchingRef = useRef(false);
  const lastFetchedKeyRef = useRef("");

  const fetchBillData = async (force: boolean = false) => {
    const companyUid = company.current?.uid;
    if (!uid || !companyUid) return;

    const requestKey = `${uid}_${companyUid}`;
    if (!force && lastFetchedKeyRef.current === requestKey) {
      return;
    }

    if (isFetchingRef.current && !force) return;
    isFetchingRef.current = true;
    lastFetchedKeyRef.current = requestKey;
    setLoading(true);

    try {
      const billResponse = await securePost('/inventory/movement/get-movement', {
        company: companyUid,
        uidMovement: uid,
        module: config.module
      });

      if (billResponse.status === 200 && billResponse.movement) {
        setBill(billResponse.movement);
        
        if (billResponse.movement.document_number) {
          const notesResponse = await securePost('/inventory/movement/get-notes-by-reference', {
            company: companyUid,
            referenceId: billResponse.movement.document_number,
            altReferenceId: billResponse.movement.electronic_invoice_number,
            module: config.module
          });
          
          if (notesResponse && notesResponse.status === 200) {
            const rawNotes = notesResponse.noteList || [];
            const uniqueMap = new Map<string, any>();
            rawNotes.forEach((el: any) => {
              const noteKey = String(el.uid || el.id || el.document_number || Math.random());
              if (!uniqueMap.has(noteKey)) {
                const reasonKey = el.correction_note_reason_key || "";
                const reason = notesReasons.find(r => r.key === reasonKey)?.string_value || "Nota a factura";
                uniqueMap.set(noteKey, {
                  ...el,
                  key: noteKey,
                  reason
                });
              }
            });
            setNotes(Array.from(uniqueMap.values()));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching bill detail:", error);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (uid && company.current?.uid) {
      fetchBillData();
    }
  }, [uid, company.current?.uid]);

  // Validación de empresa: la factura debe pertenecer a la empresa activa
  useEffect(() => {
    if (bill) {
      validateResourceCompany(
        bill.company_id,
        (bill as any).company_uid || (bill as any).companies?.uid,
        '/sale-support',
        'documento de venta'
      );
    }
  }, [bill, validateResourceCompany]);

  const handleSendEmail = async () => {
    if (!bill?.electronic_invoice_number) {
      return message.error("No hay número de documento electrónico asociado");
    }
    const docTitle = getElectronicDocumentTitle(bill.document_type);
    setSendingEmail(true);
    try {
      const res = await securePost('/electronic-invoicing/send-email', {
        company: company.current.uid,
        billNumber: bill.electronic_invoice_number,
        documentType: bill.document_type,
        email: emailTarget || undefined,
        module: config.module
      });
      if (res.status === 200) {
        message.success(res.message || `${docTitle} enviada por correo exitosamente`);
        setEmailModalVisible(false);
      } else {
        message.error(res.message || `Error al enviar la ${docTitle.toLowerCase()} por correo`);
      }
    } catch (err: any) {
      message.error(err?.message || `Error al enviar la ${docTitle.toLowerCase()} por correo`);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRetryEmission = async () => {
    const docTitle = getElectronicDocumentTitle(bill?.document_type);
    setRetrying(true);
    try {
      const res = await securePost('/electronic-invoicing/retry-invoice', {
        company: company.current.uid,
        companyId: company.current.id,
        movementUid: bill?.uid,
        module: config.module
      });
      if (res.status === 200) {
        message.success(`${docTitle} emitida y validada exitosamente`);
        fetchBillData(true);
      } else {
        message.error(res.message || `No fue posible validar la ${docTitle.toLowerCase()} ante la DIAN`);
        fetchBillData(true);
      }
    } catch (err: any) {
      message.error(err?.message || "Error al reintentar emisión");
    } finally {
      setRetrying(false);
    }
  };

  const hasCompanyMismatch = Boolean(
    bill?.company_id &&
    company.current?.id &&
    company.current.id !== 0 &&
    bill.company_id !== company.current.id
  );

  if (loading || isSwitchingCompany || hasCompanyMismatch) {
    const tipText = isSwitchingCompany || hasCompanyMismatch
      ? "Validando empresa de la factura..."
      : "Cargando detalle de factura...";
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip={tipText} />
        <div style={{ marginTop: 12 }}>{tipText}</div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Title level={4}>Factura no encontrada</Title>
        <Button icon={<RollbackOutlined />} onClick={() => history.goBack()}>Volver</Button>
      </div>
    );
  }

  const labels = getDocumentLabels(bill.document_type, bill.invoice_type);

  const isPreparatory = ['PURCHASE_ORDER', 'PURCHASE_REQUEST', 'QUOTATION'].includes(String(bill.document_type || "").toUpperCase());
  const hasPendingFulfillment = !isPreparatory &&
    bill.reception_status !== 'RECEIVED' &&
    bill.item_list?.some((el: any) =>
      el.register_type !== 'SERVICE' &&
      el.track_inventory !== false &&
      (Number(el.quantity || 0) - Number(el.quantity_received || 0)) > 0
    );

  const handleGoToStorageDispatch = () => {
    const storageModule = company.current?.modules?.find(
      m => m.app === "STORAGE" || m.name === "Almacén" || m.name === "STORAGE" || m.path?.includes("storage")
    );
    const storageBaseUrl = storageModule?.path;
    if (!storageBaseUrl) {
      message.error("No se ha configurado el módulo de almacenamiento");
      return;
    }
    const targetUrl = `${storageBaseUrl}/movement/${bill.uid}?company=${company.current?.uid}`;
    window.open(targetUrl, "_blank");
  };

  const itemListData = bill?.item_list?.map((el) => {
    const qty = Number(el.quantity) || 1;
    const unitPrice = (el.unit_price !== undefined && el.unit_price !== null && !isNaN(Number(el.unit_price)))
      ? Number(el.unit_price)
      : (((Number(el.total_amount) || 0) / qty) || 0);
    const baseAmount = el.total_amount !== undefined && el.total_amount !== null && !isNaN(Number(el.total_amount))
      ? Number(el.total_amount)
      : roundValue(unitPrice * qty);
    const discountAmount = Number(el.discount_amount) || 0;
    const taxAmount = Number(el.total_tax_amount) || 0;
    const calculatedNet = roundValue(baseAmount - discountAmount + taxAmount);
    const client_amount = calculatedNet || Number(el.total_amount_net) || Number(el.total_amount_full) || 0;
    const client_unit_price = unitPrice;

    return {
      ...el,
      id_view: el.custom_id || el.key || el.uid,
      client_amount,
      client_unit_price,
      total_tax_amount: taxAmount,
      discount_amount: discountAmount
    };
  }) || [];

  const totalDoc = Number(bill.total_amount_net || bill.total_amount || 0);
  const totalPaid = Number(
    bill.total_paid_amount ?? (bill.payments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
  );
  const balanceDue = Number(
    bill.balance_due_amount ?? Math.max(0, Math.round(((totalDoc - totalPaid) + Number.EPSILON) * 100) / 100)
  );

  const { isSettled, deliveryStatus, paymentStatus } = getSaleGeneralStatus(bill);

  const documentData: TReceiptData = {
    receiptId: bill.document_number || "",
    date: formatDateTime(bill.document_date || bill.created_at),
    customer: (bill.contact_document || "") + "-" + (bill.contact_name || "CONSUMIDOR FINAL"),
    subtotal: (bill.total_amount || 0),
    total: bill.total_amount_net || 0,
    totalDiscount: bill.total_discount_amount || 0,
    companyName: company.current.name,
    companyAddress: company.current.address || "Dirección no reportada",
    tax: bill.total_tax_amount || 0,
    taxesList: bill.taxes_list || [],
    documentTitle: labels.title,
    counterpartyLabel: labels.counterparty,
    userLabel: labels.user,
    message: bill.document_type === 'SALE_INVOICE' ? "Gracias por su compra." : "",
    items: (bill.item_list || []).map(el => {
      const qty = Number(el.quantity) || 1;
      const unitPrice = (el.unit_price !== undefined && el.unit_price !== null && !isNaN(Number(el.unit_price)))
        ? Number(el.unit_price)
        : (((Number(el.total_amount) || 0) / qty) || 0);
      const baseAmount = el.total_amount !== undefined && el.total_amount !== null && !isNaN(Number(el.total_amount))
        ? Number(el.total_amount)
        : roundValue(unitPrice * qty);
      const discount = Number(el.discount_amount) || 0;
      const taxAmount = Number(el.total_tax_amount) || 0;
      const totalNet = roundValue(baseAmount - discount + taxAmount);

      return {
        product: el.name || el.item_name || "",
        quantity: el.quantity || 0,
        price: qty ? roundValue(totalNet / qty) : totalNet,
        total: totalNet,
        discount: discount,
        taxAmount: taxAmount,
        baseAmount: baseAmount
      };
    }),
    seller: `${bill.approved_by_user_document || ""}-${bill.approved_by_user_name || ""}`,
    pdv: bill.pos_name || ""
  };

  const isBillStatusEligibleForNote = bill.status === 'COMPLETED' || bill.status === 'APPROVED';
  const hasAnnulmentNote = notes.some((el: any) => isFullReturnOrAnnulment(el.correction_note_reason_key, el.correction_note_reason_code, el.reason || el.correction_note_reason_description));
  const isCreateNoteDisabled = !isBillStatusEligibleForNote || bill.document_type !== 'SALE_INVOICE' || hasAnnulmentNote;

  return (
    <div style={{ padding: 24 }}>
      <BillDetailHeaderBar
        title={labels.title}
        documentNumber={bill.document_number}
        status={bill.status}
        isSettled={isSettled}
        documentData={documentData}
        isCreateNoteDisabled={isCreateNoteDisabled}
        onBack={() => history.goBack()}
        onCreateNote={() => history.push(`/create-note/${bill.uid}`)}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={16} lg={17}>
          <BillDetailItemsCard
            title={labels.title}
            isSaleInvoice={bill.document_type === 'SALE_INVOICE'}
            referenceDocument={bill.reference_document}
            referenceDocumentUid={bill.reference_document_uid}
            onNavigateOrigin={(origUid) => history.push(`/bill-detail/${origUid}`)}
            itemListData={itemListData}
            hasPendingFulfillment={Boolean(hasPendingFulfillment)}
            documentData={documentData}
          />

          {bill.document_type === "SALE_INVOICE" && (
            <BillDetailNotesCard
              notes={notes}
              onNavigateNote={(noteUid) => history.push(`/bill-detail/${noteUid}`)}
            />
          )}

          <BillDetailPaymentsCard
            payments={bill.payments || []}
            balanceDue={balanceDue}
            paymentOptions={register.paymentOptions}
            onOpenPaymentModal={() => setPaymentModalVisible(true)}
          />
        </Col>

        <Col xs={24} md={8} lg={7}>
          <BillDetailGeneralStatusCard
            isSettled={isSettled}
            deliveryStatus={deliveryStatus}
            hasPendingFulfillment={Boolean(hasPendingFulfillment)}
            onGoToStorageDispatch={handleGoToStorageDispatch}
            totalDoc={totalDoc}
            totalPaid={totalPaid}
            balanceDue={balanceDue}
            paymentStatus={paymentStatus}
            onOpenPaymentModal={() => setPaymentModalVisible(true)}
          />

          <BillDetailElectronicInvoiceCard
            bill={bill}
            retrying={retrying}
            onRetryEmission={handleRetryEmission}
            onOpenEmailModal={() => {
              setEmailTarget("");
              setEmailModalVisible(true);
            }}
          />

          <BillDetailGeneralInfoCard
            counterpartyLabel={labels.counterparty}
            userLabel={labels.user}
            contactName={bill.contact_name}
            contactDocument={bill.contact_document}
            approvedByUserName={bill.approved_by_user_name}
            posName={bill.pos_name}
            documentDate={bill.document_date || bill.created_at}
            paymentTerm={bill.payment_term}
          />
        </Col>
      </Row>

      <BillDetailEmailModal
        visible={emailModalVisible}
        documentType={bill.document_type}
        electronicInvoiceNumber={bill.electronic_invoice_number}
        emailTarget={emailTarget}
        onChangeEmailTarget={setEmailTarget}
        onSendEmail={handleSendEmail}
        onClose={() => setEmailModalVisible(false)}
        sendingEmail={sendingEmail}
      />

      <RegisterBillPaymentModal
        visible={paymentModalVisible}
        onClose={() => setPaymentModalVisible(false)}
        movement={bill}
        onSuccess={() => {
          fetchBillData(true);
        }}
      />
    </div>
  );
}

export default ViewBillDetail;
